/**
 * Calendrier, nombres et devise : ce qui change de forme d'une langue à l'autre
 * sans jamais être une phrase.
 *
 * Ces tables ne sont pas dans les fichiers de messages, et c'est délibéré : un
 * nom de mois n'est pas une formulation qu'on relit, c'est une donnée de
 * référence. La laisser dans le JSON, c'est ouvrir la porte à « aout » un jour
 * et « août » le lendemain, et faire échouer la vérification de parité des clés
 * sur douze entrées qui ne diront jamais rien de neuf.
 *
 * Rien ne passe par `Intl` : Hermes n'embarque pas le même ICU sur tous les
 * appareils — et aucun ICU embarqué ne connaît les mois malgaches. Une table
 * écrite ici rend la même chaîne sur tous les téléphones, ce qui est la seule
 * garantie qui vaille pour un reçu ou une date de rendez-vous.
 *
 * **Le jour vient toujours avant le mois**, dans les trois langues, y compris en
 * anglais (« 12 August 2026 », « 12/08/2026 »). Écrire l'anglais à l'américaine
 * rendrait « 03/04 » illisible pour l'acheteur comme pour le vendeur, sur le
 * même écran ; une seule lecture pour toute l'application vaut mieux qu'un
 * usage national.
 */
import type { Locale } from '@/shared/i18n/locales';

/** Les douze mois, alignés sur `Date#getMonth()`. */
type Months = readonly [
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
];

/** Les sept jours, alignés sur `Date#getDay()` — dimanche en tête. */
type Weekdays = readonly [string, string, string, string, string, string, string];

export interface CalendarNames {
  months: Months;
  /** Forme abrégée, pour les listes serrées (« 12 août », « 12 aog. »). */
  monthsShort: Months;
  weekdays: Weekdays;
  weekdaysShort: Weekdays;
  /** Ce qui relie une date à une heure : « 12 août **à** 14:30 ». */
  atTime: string;
  /** Séparateur des milliers. */
  group: string;
  /** Séparateur décimal. */
  decimal: string;
}

/**
 * Espace insécable. Un montant ne se coupe pas en fin de ligne : « 1 500 » sur
 * deux lignes se lit comme deux nombres.
 */
const NBSP = ' ';

export const CALENDAR: Record<Locale, CalendarNames> = {
  fr: {
    months: [
      'janvier',
      'février',
      'mars',
      'avril',
      'mai',
      'juin',
      'juillet',
      'août',
      'septembre',
      'octobre',
      'novembre',
      'décembre',
    ],
    monthsShort: [
      'janv.',
      'févr.',
      'mars',
      'avr.',
      'mai',
      'juin',
      'juil.',
      'août',
      'sept.',
      'oct.',
      'nov.',
      'déc.',
    ],
    weekdays: ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'],
    weekdaysShort: ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'],
    atTime: 'à',
    group: NBSP,
    decimal: ',',
  },

  // Les mois et les jours malgaches s'écrivent en minuscules, comme en
  // français : ce sont des noms communs. Les jours viennent de l'arabe par le
  // swahili (alatsinainy, talata, alarobia…), et sont les mêmes dans tout le
  // pays — c'est le calendrier que lit un vendeur d'Antananarivo comme un
  // acheteur de Toamasina.
  mg: {
    months: [
      'janoary',
      'febroary',
      'martsa',
      'aprily',
      'mey',
      'jona',
      'jolay',
      'aogositra',
      'septambra',
      'oktobra',
      'novambra',
      'desambra',
    ],
    monthsShort: [
      'jan.',
      'feb.',
      'mar.',
      'apr.',
      'mey',
      'jona',
      'jol.',
      'aog.',
      'sept.',
      'okt.',
      'nov.',
      'des.',
    ],
    weekdays: ['alahady', 'alatsinainy', 'talata', 'alarobia', 'alakamisy', 'zoma', 'asabotsy'],
    weekdaysShort: ['alah.', 'alats.', 'tal.', 'alar.', 'alak.', 'zom.', 'asab.'],
    // « amin'ny » introduit l'heure : « 12 aogositra amin'ny 14:30 ».
    atTime: "amin'ny",
    group: NBSP,
    decimal: ',',
  },

  en: {
    months: [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ],
    monthsShort: [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ],
    weekdays: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    weekdaysShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    atTime: 'at',
    group: ',',
    decimal: '.',
  },
};

/**
 * La devise s'écrit après le montant dans les trois langues.
 *
 * L'ariary n'a pas de symbole international : « Ar » est ce que portent les
 * billets, les étiquettes et les reçus du pays. On ne l'anglicise pas en
 * « MGA », que personne n'utilise à la caisse.
 */
export const CURRENCY_SUFFIX = 'Ar';
